import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { JobState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { debounce, isUndefined, uniqBy } from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  FILTER_MENU_ITEM_HEIGHT_IN_PX,
  FilterMenuItemLoadingReason,
  FilterMenuOption,
} from '~components/Filters/FilterMenuItem';
import SearchField from '~components/Filters/SearchField';
import useFetchers, { getEquipmentLabel } from '~components/Filters/useFetchers';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { Job } from '~hooks/useJob/models';
import { useStores } from '~store';

import InfiniteScrollLoadingIndicator from './InfiniteScrollLoadingIndicator';

type TypeaheadedEquipmentsQuery = {
  cursor?: string;
  loading: FilterMenuItemLoadingReason | false;
  nextPageCursor?: string;
  options: FilterMenuOption[];
  searchValue: string;
};

interface EquipmentSelectorProps {
  companyId: string;
  equipment: Job['equipment'];
  job: Job;
  onChange: (equipmentId?: string) => void;
  placeholder: string;
  allowRemovalInActiveStates?: boolean;
}

const equipmentsQueryInitialState: TypeaheadedEquipmentsQuery = {
  loading: false,
  searchValue: '',
  options: [],
};

export const jobStatesAllowingEquipmentRemoval = [
  JobState.ACCEPTED,
  JobState.ASSIGNED,
  JobState.CREATED,
  JobState.SENT,
];

export default function EquipmentSelector({
  companyId,
  equipment,
  job,
  onChange,
  placeholder,
  allowRemovalInActiveStates = false,
}: EquipmentSelectorProps) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchor);

  const { ordersDispatchStore } = useStores();
  const { fetchJobEquipments } = useFetchers(companyId);
  const fetchEquipments = fetchJobEquipments(job.id);
  const removeEquipmentRef = useRef<SVGSVGElement | null>(null);

  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [equipmentsQuery, setEquipmentsQuery] = useState(equipmentsQueryInitialState);

  const isEquipmentRemovable = Boolean(
    equipment &&
    (allowRemovalInActiveStates ||
      jobStatesAllowingEquipmentRemoval.includes(job.status)),
  );

  const isOpenAndEditable = isOpen && job.editable;
  const allEquipmentsFetched =
    'nextPageCursor' in equipmentsQuery && isUndefined(equipmentsQuery.nextPageCursor);
  const hasMore = !allEquipmentsFetched;

  const fetch = async () => {
    try {
      if (!hasMore) {
        return;
      }

      const response = await fetchEquipments(
        equipmentsQuery.searchValue,
        equipmentsQuery.cursor,
      );

      setEquipmentsQuery((state) => ({
        ...state,
        loading: false,
        options: uniqBy(state.options.concat(response.data), (i) => i.value),
        nextPageCursor: response.nextPageCursor,
      }));
    } catch {
      setEquipmentsQuery((state) => ({
        ...state,
        loading: false,
        nextPageCursor: undefined,
      }));
    }
  };

  const handleFetchMore = useCallback(() => {
    if (!hasMore || !isOpenAndEditable) {
      return undefined;
    }

    setEquipmentsQuery((state) => {
      if (state.loading) {
        return state;
      }

      return {
        ...state,
        loading: FilterMenuItemLoadingReason.INFINITE_SCROLL,
        cursor: state.nextPageCursor,
      };
    });
  }, [hasMore, isOpenAndEditable]);

  useEffect(() => {
    if (equipmentsQuery.loading) {
      fetch();
    }
  }, [equipmentsQuery.loading]);

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      setEquipmentsQuery(() => ({
        loading: FilterMenuItemLoadingReason.SEARCH_VALUE,
        options: [],
        searchValue: searchFieldValue,
      }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

    if (isOpenAndEditable) {
      debouncedSearch();
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [isOpenAndEditable, searchFieldValue]);

  useEffect(() => {
    if (!isOpenAndEditable) {
      setSearchFieldValue('');
      setEquipmentsQuery(equipmentsQueryInitialState);
    }
  }, [isOpenAndEditable]);

  if (!job.editable) {
    if (equipment) {
      return <OverflowAwareText>{getEquipmentLabel(equipment)}</OverflowAwareText>;
    }

    return null;
  }

  return (
    <>
      <ButtonBase
        disabled={Boolean(ordersDispatchStore.isUpdatingOrderJob.get(job.id))}
        sx={{
          alignItems: 'center',
          border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          borderRadius: theme.spacing(0.5),
          gap: 0.5,
          height: 24,
          maxWidth: '100%',
          pl: 0.5,
          pr: equipment ? 0.5 : 0,
          backgroundColor: theme.semantic.surfaceRaised,
          '&.Mui-disabled': { opacity: 0.6, pointerEvents: 'auto' },
          '&:hover': {
            backgroundColor: theme.brandV2.colors.treadGray10,
            borderColor: theme.brandV2.colors.treadGray3,
            '& .MuiSvgIcon-root': isEquipmentRemovable
              ? {
                  '&:hover': {
                    backgroundColor: theme.brandV2.colors.treadGray3,
                    color: theme.semantic.textInverse,
                  },
                }
              : { color: theme.brandV2.colors.treadGray2 },
          },
        }}
        onClick={(event) => {
          event.stopPropagation();

          if (event.target === removeEquipmentRef.current) {
            setAnchor(null);
            onChange();
          } else {
            setAnchor(event.currentTarget);
          }
        }}
      >
        <OverflowAwareText
          sx={{
            flex: 1,
            color: equipment
              ? theme.brandV2.colors.treadBlack
              : theme.brandV2.colors.treadGray4,
          }}
        >
          {equipment ? getEquipmentLabel(equipment) : placeholder}
        </OverflowAwareText>

        {isEquipmentRemovable ? (
          <Tooltip title={t('actions.remove_entity', { entity: placeholder })}>
            <Close
              ref={removeEquipmentRef}
              sx={{
                backgroundColor: 'transparent',
                borderRadius: '50%',
                color: theme.brandV2.colors.treadGray2,
                flex: 0,
                fontSize: '12px',
                height: '13px',
                minWidth: '13px',
                width: '13px',
              }}
            />
          </Tooltip>
        ) : (
          <ArrowDropDown
            sx={{
              color: theme.brandV2.colors.treadGray4,
              flex: 0,
              fontSize: '18px',
              minWidth: '18px',
            }}
          />
        )}
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={isOpen}
        onClose={(event: React.MouseEvent) => {
          event.stopPropagation();
          setAnchor(null);
        }}
        sx={{
          '& .MuiPaper-root': {
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            boxShadow: '0px 1px 10px 0px rgba(60, 64, 67, 0.15)',
            mt: '3px',
            width: '250px',
          },
          '& .MuiItem-root': {
            px: 1.5,
          },
        }}
      >
        <SearchField
          isLoading={equipmentsQuery.loading === FilterMenuItemLoadingReason.SEARCH_VALUE}
          onChange={(value) => setSearchFieldValue(value)}
          setFocusOnMount
          value={searchFieldValue}
        />

        <Divider sx={{ '&.MuiDivider-root': { mt: 0, mb: 1, mx: 2 } }} />

        {equipmentsQuery.options.length > 0 && (
          <Box
            sx={{
              maxHeight: FILTER_MENU_ITEM_HEIGHT_IN_PX * 5,
              overflow: 'auto',
            }}
          >
            {equipmentsQuery.options.map((availableEquipment, index) => (
              <React.Fragment key={availableEquipment.value}>
                <MenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    setAnchor(null);
                    onChange(availableEquipment.value);
                  }}
                  data-ui={`page-dispatch__equipment-selector__equipment-${availableEquipment.value}__select-button`}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <OverflowAwareText>{availableEquipment.label}</OverflowAwareText>

                    {availableEquipment.value === equipment?.id && (
                      <Check sx={{ fontSize: '16px' }} />
                    )}
                  </Box>
                </MenuItem>

                {index === equipmentsQuery.options.length - 1 && hasMore && (
                  <InfiniteScrollLoadingIndicator
                    alignItems="center"
                    display="flex"
                    height={FILTER_MENU_ITEM_HEIGHT_IN_PX}
                    isLoading={
                      equipmentsQuery.loading ===
                      FilterMenuItemLoadingReason.INFINITE_SCROLL
                    }
                    justifyContent="center"
                    onFetchMore={handleFetchMore}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Menu>
    </>
  );
}
